import React, { useRef, useEffect } from 'react';

import PropTypes from 'prop-types';

import { useLocale } from '@adsk/alloy-react-locale';
import Panel from '@adsk/alloy-react-panel';
import PackagesSearch from '@adsk/alloy-react-search-field';
import theme from '@adsk/alloy-react-theme';

import PackagesFilter from './PackagesFilter';
import {
  StyledSearchFilterPanelFooter,
  StyledSearchButton,
} from './PackagesSearchFilterPanel.styles';
import { usePackagesSearchFilter } from './PackagesSearchFilterProvider';
import getIsValidSearchFilter from '../helpers/getIsValidSearchFilter';

const PackagesSearchFilterPanel = ({ open, setFilters }) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const { panelSearch, setPanelSearch, panelFilters, setPanelFilters } =
    usePackagesSearchFilter();
  const searchField = useRef(null);

  useEffect(() => {
    if (open) {
      window.setTimeout(() => searchField.current?.focus?.(), 250);
    }
  }, [open]);

  // Reset filter panel context each time the picker modal is closed
  useEffect(() => {
    setPanelSearch('');
    setPanelFilters(() => ({}));
  }, [setPanelFilters, setPanelSearch]);

  const isValidSearchFilter = getIsValidSearchFilter({
    panelSearch,
    panelFilters,
  });

  const handleSearch = () => {
    if (!isValidSearchFilter) return;

    // 1. Reference picker uses searchExpression inside, rename panelSearch to keep consistent
    // 2. Destruct panelFilters since reference picker calls `queryEntities`
    //    by checking serialized filters object
    setFilters({ searchExpression: panelSearch, ...panelFilters });
  };

  const renderFooter = () => {
    return (
      <StyledSearchFilterPanelFooter>
        <StyledSearchButton
          disabled={!isValidSearchFilter}
          onClick={handleSearch}
        >
          {T.translate('BUTTON.SEARCH')}
        </StyledSearchButton>
      </StyledSearchFilterPanelFooter>
    );
  };

  return (
    <Panel
      open={open}
      width={Panel.WIDTHS.MEDIUM}
      renderHeader={() => null}
      renderFooter={renderFooter}
    >
      <Panel.Body style={{ overflow: 'auto', fontSize: '12px' }}>
        <PackagesSearch
          ref={searchField}
          style={{
            marginBottom: '16px',
            'input:disabled': {
              background: theme.colors.white,
            },
          }}
          placeholder={T.translate('SEARCH.PLACEHOLDER')}
          value={panelSearch}
          onChange={(value) => setPanelSearch(value)}
          onSearch={handleSearch}
        />
        <PackagesFilter />
      </Panel.Body>
    </Panel>
  );
};

PackagesSearchFilterPanel.propTypes = {
  /**
   * Whether the panel is open
   */
  open: PropTypes.bool,

  /**
   * Callback to set filters.
   */
  setFilters: PropTypes.func,
};

export default PackagesSearchFilterPanel;
